import { observer } from 'mobx-react'
import React, { useState } from 'react'
import styled from 'styled-components'
import Close from '../icons/Close'
import User from '../state/User'
import { IconButton } from './Button'
import LinkExternal from './LinkExternal'

export const RADOM_V2_ANNOUNCEMENT_URL = 'https://www.radom.com/insights/introducing-radom-2-0'

const DISMISSED_KEY_PREFIX = 'radom-v2-banner-dismissed:'

const dismissedKey = (organizationId: string): string => `${DISMISSED_KEY_PREFIX}${organizationId}`

// localStorage can throw (private windows, blocked site data). A failed read
// shows the banner; a failed write just lets it come back next time.
const wasDismissed = (organizationId: string): boolean => {
  try {
    return localStorage.getItem(dismissedKey(organizationId)) === '1'
  } catch {
    return false
  }
}

const rememberDismissed = (organizationId: string): void => {
  try {
    localStorage.setItem(dismissedKey(organizationId), '1')
  } catch {}
}

const Bar = styled.div`
  align-items: center;
  background: var(--surface-muted, #f8fafc);
  border-bottom: 1px solid var(--surface-pressed, #edf1f7);
  color: var(--ink, #20242d);
  display: flex;
  font-family: var(--font-sans);
  font-size: 13px;
  gap: 12px;
  line-height: 1.5;
  min-height: 44px;
  padding: 7px 12px 7px 18px;
  width: 100%;
`

// One flowing sentence: the pill and the link sit inline so the bar wraps
// naturally on narrow screens instead of breaking into stacked pieces.
const Message = styled.p`
  color: var(--text-muted, #6b7280);
  flex: 1 1 auto;
  margin: 0;
  min-width: 0;
`

const Pill = styled.span`
  align-items: center;
  background: var(--primary-btn-bg, #171717);
  border-radius: 999px;
  color: var(--primary-btn-ink, #ffffff);
  display: inline-flex;
  font-size: 10.5px;
  font-weight: 560;
  letter-spacing: 0.02em;
  line-height: 1;
  margin-right: 9px;
  padding: 5px 8px;
  vertical-align: 1px;
  white-space: nowrap;
`

const Headline = styled.span`
  color: var(--ink, #20242d);
  font-weight: 550;
`

const ReadLink = styled.a`
  align-items: center;
  border-radius: 6px;
  color: var(--ink, #20242d);
  display: inline-flex;
  font-weight: 500;
  gap: 4px;
  margin-left: 6px;
  text-decoration: underline;
  text-decoration-color: var(--border-strong, #d6dae2);
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
  transition: text-decoration-color 0.16s ease;
  white-space: nowrap;

  svg {
    opacity: 0.65;
    transition: opacity 0.16s ease;
  }

  &:hover {
    text-decoration-color: currentColor;

    svg {
      opacity: 1;
    }
  }

  &:focus-visible {
    box-shadow: 0 0 0 4px var(--focus-ring, rgba(17, 17, 17, 0.05));
    outline: 0;
  }
`

const Dismiss = styled(IconButton)`
  flex: 0 0 auto;
  padding: 6px;
`

interface IProps {
  /** Extra space above the bar, in pixels, so it clears the fixed top bar where the layout's spacer falls short. */
  offsetTop?: number
}

const RadomV2Banner = observer(({ offsetTop = 0 }: IProps): React.ReactElement | null => {
  const [dismissedIds, setDismissedIds] = useState<string[]>([])

  const organization = User.organization
  const organizationId = organization?.organizationId

  // Nothing until the selected organization has actually loaded: the store
  // can still hold the previous organization while a switch is in flight.
  if (!organization || !organizationId || organizationId !== User.selectedOrganizationId) {
    return null
  }

  if (!organization.features?.isUnifiedTreasuryEnabled) {
    return null
  }

  if (dismissedIds.includes(organizationId) || wasDismissed(organizationId)) {
    return null
  }

  const dismiss = (): void => {
    rememberDismissed(organizationId)
    setDismissedIds(ids => [...ids, organizationId])
  }

  return <Bar
    role='region'
    aria-label='Radom v2.0 announcement'
    style={offsetTop > 0 ? { marginTop: offsetTop } : undefined}>
    <Message>
      <Pill>New</Pill>
      <Headline>Radom v2.0 is here:</Headline>
      {' '}one balance across every network, conversions, and payouts in any asset from any asset.
      <ReadLink
        href={RADOM_V2_ANNOUNCEMENT_URL}
        target='_blank'
        rel='noopener noreferrer'>
        Read all about it
        <LinkExternal width={12} height={12} stroke='currentColor' />
      </ReadLink>
    </Message>
    <Dismiss
      type='button'
      aria-label='Dismiss announcement'
      title='Dismiss'
      onClick={dismiss}>
      <Close width={15} height={15} />
    </Dismiss>
  </Bar>
})

export default RadomV2Banner
